import { Command } from 'commander';
import { ConfigService } from '../../services/config.service.js';
import { ValidationError } from '../../errors/index.js';
import { promises as fs } from 'fs';
import { join } from 'path';
import { homedir } from 'os';
import type { InfoOptions } from '../../types/framework.types.js';
import { getCliName } from '../../utils/cli-name.js';

/**
 * Create info command for Jai1 Framework
 * Shows configuration and status information
 */
export function createInfoCommand(): Command {
    const cmd = new Command('info')
        .description('Show client configuration and status')
        .option('--json', 'Output as JSON')
        .option('--verbose', 'Show detailed information')
        .action(async (options: InfoOptions) => {
            await handleInfo(options);
        });

    return cmd;
}

async function handleInfo(options: InfoOptions): Promise<void> {
    const configService = new ConfigService();
    const config = await configService.load();

    if (!config) {
        throw new ValidationError(`Not initialized. Run "${getCliName()} auth" first.`);
    }

    const frameworkPath = join(homedir(), '.jai1', 'framework');
    const projectStatus = await getProjectStatus();

    const info = {
        configPath: configService.getConfigPath(),
        apiUrl: config.apiUrl,
        accessKey: maskKey(config.accessKey),
        version: config.version || 'Not downloaded',
        lastUpdated: config.lastUpdated || 'Never',
        frameworkPath,
        projectStatus,
    };

    if (options.json) {
        console.log(JSON.stringify(info, null, 2));
        return;
    }

    console.log('Jai1 Client Info');
    console.log('────────────────\n');
    console.log(`Config Location:    ${info.configPath}`);
    console.log(`API URL:            ${info.apiUrl}`);
    console.log(`Access Key:         ${info.accessKey}`);
    console.log(`Framework Version:  ${info.version}`);
    console.log(`Last Updated:       ${info.lastUpdated}`);
    console.log(`Framework Location: ${info.frameworkPath}`);
    console.log();
    console.log('Project Status:');
    console.log(`  .jai1/ exists:    ${info.projectStatus.exists ? '✓' : '✗'}`);
    if (info.projectStatus.exists) {
        console.log(`  Synced version:   ${info.projectStatus.version || 'Unknown'}`);
    }
}

function maskKey(key: string): string {
    if (key.length <= 4) return '****';
    return '****' + key.slice(-4);
}

async function getProjectStatus(): Promise<{ exists: boolean; version?: string }> {
    const projectJai1 = join(process.cwd(), '.jai1');
    try {
        await fs.access(projectJai1);
        // Try to read version from project
        return { exists: true, version: 'Synced' };
    } catch {
        return { exists: false };
    }
}
